import React, { ComponentPropsWithoutRef, useMemo } from 'react';
import { SidebarSectionEnum } from './enums';
import { Button } from 'antd';
import { StoreDetails } from '../../../types/store';
import classnames from 'classnames';
import collect from 'collect.js';

type Props = {
  data: StoreDetails;
} & ComponentPropsWithoutRef<'div'>;

const LABEL_BY_SECTION_ID: Record<SidebarSectionEnum, string> = {
  restaurant_info: 'Restaurant Info',
  order_method_info: 'Delivery and Pickup',
  payment_methods: 'Payment methods',
  catering: 'Catering',
  dietary_info: 'Dietary Info',
  important_notes: 'Important Notes',
  reservations: 'Reservations',
  promoted_products: 'Top seller',
} as const;

const SectionsNavbar = ({ data, className, ...rest }: Props) => {
  const availableSections = useMemo(
    () =>
      collect(Object.values(SidebarSectionEnum))
        .when(!data?.promoted_products?.length, (collection) =>
          collection.reject((section) => section === SidebarSectionEnum.PromotedProducts),
        )
        .when(!data?.notes?.length, (collection) =>
          collection.reject((section) => section === SidebarSectionEnum.ImportantNotes),
        ),
    [],
  );
  const onClick = (section: SidebarSectionEnum) => {
    function scrollIntoViewInContainer(target: HTMLElement, container: HTMLElement, offset = 0) {
      if (!target || !container) {
        return;
      }

      const cRect = container.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();
      const y = tRect.top - cRect.top + container.scrollTop - offset;
      container.scrollTo({ top: y, behavior: 'smooth' });
    }

    // Usage:
    const container = document.getElementById('sidebar');
    const target = document.getElementById(section);
    const header: HTMLElement = container.querySelector('#sidebar-header');
    const offset = header ? header.offsetHeight + 5 : 0;

    scrollIntoViewInContainer(target, container, offset);
  };

  return (
    <div id="sidebar-header" className={classnames('flex flex-wrap gap-sm', className)} {...rest}>
      {availableSections.map((section) => (
        <Button key={section} rootClassName="text-xs font-medium" onClick={() => onClick(section)}>
          {LABEL_BY_SECTION_ID[section]}
        </Button>
      ))}
    </div>
  );
};

export default SectionsNavbar;
